const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const toMessengerV2ServerSeq = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

export const resolveMessengerV2SelfReceiptState = (
  item,
  { selfConversation = false, mine = item?.mine } = {},
) => {
  if (!selfConversation) return null;
  // Sender and recipient are the same account. Read here means its own rendered,
  // server-acknowledged message, not delivery to every device on that account.
  const acknowledged = Boolean(
    mine &&
      !item?.optimistic &&
      !item?.localPendingUpload &&
      ['sent', 'plain', 'mls_decrypted', 'history_decrypted', 'delivered', 'read', 'seen'].includes(
        item?.state,
      ) &&
      (toStringOrNull(item?.serverMsgId ?? item?.server_msg_id) ||
        toMessengerV2ServerSeq(item?.serverSeq ?? item?.server_seq)),
  );
  return { delivered: acknowledged, read: acknowledged };
};

export const normalizeMessengerV2ReceiptFrontier = (frontier = null) => ({
  serverMsgId: toStringOrNull(
    frontier?.serverMsgId ?? frontier?.server_msg_id ?? frontier?.messageId ?? null,
  ),
  serverSeq: toMessengerV2ServerSeq(frontier?.serverSeq ?? frontier?.server_seq ?? null),
});

const comparableMessageId = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

export const compareMessengerV2ReceiptFrontiers = (left = null, right = null) => {
  const normalizedLeft = normalizeMessengerV2ReceiptFrontier(left);
  const normalizedRight = normalizeMessengerV2ReceiptFrontier(right);

  if (normalizedLeft.serverSeq != null || normalizedRight.serverSeq != null) {
    if (normalizedLeft.serverSeq == null) return -1;
    if (normalizedRight.serverSeq == null) return 1;
    return normalizedLeft.serverSeq - normalizedRight.serverSeq;
  }

  const leftMessageId = comparableMessageId(normalizedLeft.serverMsgId);
  const rightMessageId = comparableMessageId(normalizedRight.serverMsgId);
  if (leftMessageId != null || rightMessageId != null) {
    if (leftMessageId == null) return -1;
    if (rightMessageId == null) return 1;
    return leftMessageId - rightMessageId;
  }

  if (normalizedLeft.serverMsgId === normalizedRight.serverMsgId) return 0;
  if (!normalizedLeft.serverMsgId) return -1;
  if (!normalizedRight.serverMsgId) return 1;
  return null;
};

export const selectMessengerV2AdvancedFrontier = (...frontiers) =>
  frontiers.reduce((selected, candidate) => {
    const normalizedCandidate = normalizeMessengerV2ReceiptFrontier(candidate);
    if (!normalizedCandidate.serverMsgId && normalizedCandidate.serverSeq == null) {
      return selected;
    }
    if (!selected) return normalizedCandidate;

    const comparison = compareMessengerV2ReceiptFrontiers(normalizedCandidate, selected);
    if (comparison > 0) return normalizedCandidate;
    if (comparison < 0 || comparison == null) return selected;

    return {
      serverMsgId: normalizedCandidate.serverMsgId || selected.serverMsgId,
      serverSeq: normalizedCandidate.serverSeq ?? selected.serverSeq,
    };
  }, null) || { serverMsgId: null, serverSeq: null };

const isRenderableReceiptItem = (item = null) =>
  item?.state !== 'pending_runtime' && item?.state !== 'decrypt_failed';

export const resolveMessengerV2TimelineFrontier = (
  items = [],
  { remoteOnly = false, renderableOnly = false } = {},
) =>
  (Array.isArray(items) ? items : []).reduce((frontier, item) => {
    if (!item || (remoteOnly && item.mine === true)) return frontier;
    if (renderableOnly && !isRenderableReceiptItem(item)) return frontier;
    return selectMessengerV2AdvancedFrontier(frontier, item);
  }, null) || { serverMsgId: null, serverSeq: null };

export const resolveMessengerV2VisibleReceiptFrontier = ({
  timelineItems = [],
  frontierServerMsgId = null,
  frontierServerSeq = null,
  remoteOnly = false,
  renderableOnly = false,
} = {}) =>
  selectMessengerV2AdvancedFrontier(
    resolveMessengerV2TimelineFrontier(timelineItems, { remoteOnly, renderableOnly }),
    renderableOnly
      ? null
      : {
          serverMsgId: frontierServerMsgId,
          serverSeq: frontierServerSeq,
        },
  );

export const messengerV2FrontierCovers = (known = null, target = null) => {
  const normalizedTarget = normalizeMessengerV2ReceiptFrontier(target);
  if (!normalizedTarget.serverMsgId && normalizedTarget.serverSeq == null) return true;
  const comparison = compareMessengerV2ReceiptFrontiers(known, normalizedTarget);
  return comparison != null && comparison >= 0;
};

export const mergeMessengerV2ReceiptFrontiers = (current = null, incoming = null) => {
  const existing = current && typeof current === 'object' ? current : {};
  const source = incoming && typeof incoming === 'object' ? incoming : {};
  const delivered = selectMessengerV2AdvancedFrontier(
    {
      serverMsgId: existing.lastDeliveredServerMsgId,
      serverSeq: existing.lastDeliveredServerSeq,
    },
    {
      serverMsgId: source.lastDeliveredServerMsgId,
      serverSeq: source.lastDeliveredServerSeq,
    },
  );
  const decrypted = selectMessengerV2AdvancedFrontier(
    {
      serverMsgId: existing.lastDecryptedServerMsgId,
      serverSeq: existing.lastDecryptedServerSeq,
    },
    {
      serverMsgId: source.lastDecryptedServerMsgId,
      serverSeq: source.lastDecryptedServerSeq,
    },
  );
  const read = selectMessengerV2AdvancedFrontier(
    {
      serverMsgId: existing.lastReadServerMsgId,
      serverSeq: existing.lastReadServerSeq,
    },
    {
      serverMsgId: source.lastReadServerMsgId,
      serverSeq: source.lastReadServerSeq,
    },
  );

  return {
    conversationId:
      toStringOrNull(source.conversationId) || toStringOrNull(existing.conversationId),
    lastDeliveredServerMsgId: delivered.serverMsgId,
    lastDecryptedServerMsgId: decrypted.serverMsgId,
    lastReadServerMsgId: read.serverMsgId,
    lastDeliveredServerSeq: delivered.serverSeq,
    lastDecryptedServerSeq: decrypted.serverSeq,
    lastReadServerSeq: read.serverSeq,
    unreadCount:
      source.unreadCount != null
        ? Math.max(0, Number(source.unreadCount) || 0)
        : existing.unreadCount != null
          ? Math.max(0, Number(existing.unreadCount) || 0)
          : null,
  };
};

export const messengerV2ReceiptCovers = (known = null, target = null) =>
  messengerV2FrontierCovers(
    {
      serverMsgId: known?.lastDeliveredServerMsgId,
      serverSeq: known?.lastDeliveredServerSeq,
    },
    {
      serverMsgId: target?.lastDeliveredServerMsgId,
      serverSeq: target?.lastDeliveredServerSeq,
    },
  ) &&
  messengerV2FrontierCovers(
    {
      serverMsgId: known?.lastDecryptedServerMsgId,
      serverSeq: known?.lastDecryptedServerSeq,
    },
    {
      serverMsgId: target?.lastDecryptedServerMsgId,
      serverSeq: target?.lastDecryptedServerSeq,
    },
  ) &&
  messengerV2FrontierCovers(
    {
      serverMsgId: known?.lastReadServerMsgId,
      serverSeq: known?.lastReadServerSeq,
    },
    {
      serverMsgId: target?.lastReadServerMsgId,
      serverSeq: target?.lastReadServerSeq,
    },
  );
